/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {ApplicationRef} from '../application_ref';
import {ChangeDetectorRef} from '../change_detection/change_detection';
import {Injector} from '../di/injector';
import {ComponentFactory, ComponentRef} from '../linker/component_factory';
import {ComponentFactoryBoundToModule, ComponentFactoryResolver} from '../linker/component_factory_resolver';
import {ElementRef} from '../linker/element_ref';
import {InternalNgModuleRef, NgModuleRef} from '../linker/ng_module_factory';
import {TemplateRef} from '../linker/template_ref';
import {ViewContainerRef} from '../linker/view_container_ref';
import {EmbeddedViewRef, InternalViewRef, ViewRef} from '../linker/view_ref';
import {Renderer as RendererV1, Renderer2} from '../render/api';
import {Type} from '../type';
import {stringify} from '../util';
import {VERSION} from '../version';

import {callNgModuleLifecycle, initNgModule, resolveNgModuleDep} from './ng_module';
import {DepFlags, ElementData, NgModuleData, NgModuleDefinition, NodeDef, NodeFlags, Services, TemplateData, ViewContainerData, ViewData, ViewDefinitionFactory, ViewState, asElementData, asProviderData, asTextData} from './types';
import {markParentViewsForCheck, resolveDefinition, rootRenderNodes, splitNamespace, tokenKey, viewParentEl} from './util';
import {attachEmbeddedView, detachEmbeddedView, moveEmbeddedView, renderDetachView} from './view_attach';

const /** @type {?} */ EMPTY_CONTEXT = new Object();
/**
 * @param {?} selector
 * @param {?} componentType
 * @param {?} viewDefFactory
 * @param {?} inputs
 * @param {?} outputs
 * @param {?} ngContentSelectors
 * @return {?}
 */
export function createComponentFactory(
    selector: string, componentType: Type<any>, viewDefFactory: ViewDefinitionFactory,
    inputs: {[propName: string]: string} | null, outputs: {[propName: string]: string},
    ngContentSelectors: string[]): ComponentFactory<any> {
  return new ComponentFactory_(
      selector, componentType, viewDefFactory, inputs, outputs, ngContentSelectors);
}
/**
 * @param {?} componentFactory
 * @return {?}
 */
export function getComponentViewDefinitionFactory(componentFactory: ComponentFactory<any>):
    ViewDefinitionFactory {
  return ( /** @type {?} */((componentFactory as ComponentFactory_))).viewDefFactory;
}
class ComponentFactory_ extends ComponentFactory<any> {
/**
 * \@internal
 */
viewDefFactory: ViewDefinitionFactory;
/**
 * @param {?} selector
 * @param {?} componentType
 * @param {?} viewDefFactory
 * @param {?} _inputs
 * @param {?} _outputs
 * @param {?} ngContentSelectors
 */
constructor(
public selector: string,
public componentType: Type<any>,
      viewDefFactory: ViewDefinitionFactory,
private _inputs: {[propName: string]: string}|null,
private _outputs: {[propName: string]: string},
public ngContentSelectors: string[]) {
    // Attention: this ctor is called as top level function.
    // Putting any logic in here will destroy closure tree shaking!
    super();
    this.viewDefFactory = viewDefFactory;
  }
/**
 * @return {?}
 */
get inputs() {
    const /** @type {?} */ inputsArr: {propName: string, templateName: string}[] = [];
    const /** @type {?} */ inputs = /** @type {?} */(( this._inputs));
    for (let /** @type {?} */ propName in inputs) {
      const /** @type {?} */ templateName = inputs[propName];
      inputsArr.push({propName, templateName});
    }
    return inputsArr;
  }
/**
 * @return {?}
 */
get outputs() {
    const /** @type {?} */ outputsArr: {propName: string, templateName: string}[] = [];
    for (let /** @type {?} */ propName in this._outputs) {
      const /** @type {?} */ templateName = this._outputs[propName];
      outputsArr.push({propName, templateName});
    }
    return outputsArr;
  }
/**
 * Creates a new component.
 * @param {?} injector
 * @param {?=} projectableNodes
 * @param {?=} rootSelectorOrNode
 * @param {?=} ngModule
 * @return {?}
 */
create(
      injector: Injector, projectableNodes?: any[][], rootSelectorOrNode?: string|any,
      ngModule?: NgModuleRef<any>): ComponentRef<any> {
    if (!ngModule) {
      throw new Error('ngModule should be provided');
    }
    const /** @type {?} */ viewDef = resolveDefinition(this.viewDefFactory);
    const /** @type {?} */ componentNodeIndex = /** @type {?} */(( /** @type {?} */(( viewDef.nodes[0].element)).componentProvider)).index;
    const /** @type {?} */ view = Services.createRootView(
        injector, projectableNodes || [], rootSelectorOrNode, viewDef, ngModule, EMPTY_CONTEXT);
    const /** @type {?} */ component = asProviderData(view, componentNodeIndex).instance;
    if (rootSelectorOrNode) {
      view.renderer.setAttribute(asElementData(view, 0).renderElement, 'ng-version', VERSION.full);
    }

    return new ComponentRef_(view, new ViewRef_(view), component);
  }
}

function ComponentFactory__tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
ComponentFactory_.prototype.viewDefFactory;
/** @type {?} */
ComponentFactory_.prototype.selector;
/** @type {?} */
ComponentFactory_.prototype.componentType;
/** @type {?} */
ComponentFactory_.prototype._inputs;
/** @type {?} */
ComponentFactory_.prototype._outputs;
/** @type {?} */
ComponentFactory_.prototype.ngContentSelectors;
}

class ComponentRef_ extends ComponentRef<any> {
private _elDef: NodeDef;
/**
 * @param {?} _view
 * @param {?} _viewRef
 * @param {?} _component
 */
constructor(private _view: ViewData,
private _viewRef: ViewRef,
private _component: any) {
    super();
    this._elDef = this._view.def.nodes[0];
  }
/**
 * @return {?}
 */
get location(): ElementRef {
    return new ElementRef(asElementData(this._view, this._elDef.index).renderElement);
  }
/**
 * @return {?}
 */
get injector(): Injector { return new Injector_(this._view, this._elDef); }
/**
 * @return {?}
 */
get instance(): any { return this._component; };
/**
 * @return {?}
 */
get hostView(): ViewRef { return this._viewRef; };
/**
 * @return {?}
 */
get changeDetectorRef(): ChangeDetectorRef { return this._viewRef; };
/**
 * @return {?}
 */
get componentType(): Type<any> { return /** @type {?} */(( <any>this._component.constructor)); }
/**
 * @return {?}
 */
destroy(): void { this._viewRef.destroy(); }
/**
 * @param {?} callback
 * @return {?}
 */
onDestroy(callback: Function): void { this._viewRef.onDestroy(callback); }
}

function ComponentRef__tsickle_Closure_declarations() {
/** @type {?} */
ComponentRef_.prototype._elDef;
/** @type {?} */
ComponentRef_.prototype._view;
/** @type {?} */
ComponentRef_.prototype._viewRef;
/** @type {?} */
ComponentRef_.prototype._component;
}

/**
 * @param {?} view
 * @param {?} elDef
 * @param {?} elData
 * @return {?}
 */
export function createViewContainerData(
    view: ViewData, elDef: NodeDef, elData: ElementData): ViewContainerData {
  return new ViewContainerRef_(view, elDef, elData);
}
class ViewContainerRef_ implements ViewContainerData {
/**
 * \@internal
 */
_embeddedViews: ViewData[] = [];
/**
 * @param {?} _view
 * @param {?} _elDef
 * @param {?} _data
 */
constructor(private _view: ViewData,
private _elDef: NodeDef,
private _data: ElementData) {}
/**
 * @return {?}
 */
get element(): ElementRef { return new ElementRef(this._data.renderElement); }
/**
 * @return {?}
 */
get injector(): Injector { return new Injector_(this._view, this._elDef); }
/**
 * @return {?}
 */
get parentInjector(): Injector {
    let /** @type {?} */ view = this._view;
    let /** @type {?} */ elDef = this._elDef.parent;
    while (!elDef && view) {
      elDef = viewParentEl(view);
      view = /** @type {?} */(( view.parent));
    }

    return view ? new Injector_(view, elDef) : new Injector_(this._view, null);
  }
/**
 * @return {?}
 */
clear(): void {
    const /** @type {?} */ len = this._embeddedViews.length;
    for (let /** @type {?} */ i = len - 1; i >= 0; i--) {
      const /** @type {?} */ view = /** @type {?} */(( detachEmbeddedView(this._data, i)));
      Services.destroyView(view);
    }
  }
/**
 * @param {?} index
 * @return {?}
 */
get(index: number): ViewRef|null {
    const /** @type {?} */ view = this._embeddedViews[index];
    if (view) {
      const /** @type {?} */ ref = new ViewRef_(view);
      ref.attachToViewContainerRef(this);
      return ref;
    }
    return null;
  }
/**
 * @return {?}
 */
get length(): number { return this._embeddedViews.length; };
/**
 * @template C
 * @param {?} templateRef
 * @param {?=} context
 * @param {?=} index
 * @return {?}
 */
createEmbeddedView<C>(templateRef: TemplateRef<C>, context?: C, index?: number):
      EmbeddedViewRef<C> {
    const /** @type {?} */ viewRef = templateRef.createEmbeddedView(context || /** @type {?} */(( <any>{})));
    this.insert(viewRef, index);
    return viewRef;
  }
/**
 * @template C
 * @param {?} componentFactory
 * @param {?=} index
 * @param {?=} injector
 * @param {?=} projectableNodes
 * @param {?=} ngModuleRef
 * @return {?}
 */
createComponent<C>(
      componentFactory: ComponentFactory<C>, index?: number, injector?: Injector,
      projectableNodes?: any[][], ngModuleRef?: NgModuleRef<any>): ComponentRef<C> {
    const /** @type {?} */ contextInjector = injector || this.parentInjector;
    if (!ngModuleRef && !(componentFactory instanceof ComponentFactoryBoundToModule)) {
      ngModuleRef = contextInjector.get(NgModuleRef);
    }
    const /** @type {?} */ componentRef =
        componentFactory.create(contextInjector, projectableNodes, undefined, ngModuleRef);
    this.insert(componentRef.hostView, index);
    return componentRef;
  }
/**
 * @param {?} viewRef
 * @param {?=} index
 * @return {?}
 */
insert(viewRef: ViewRef, index?: number): ViewRef {
    if (viewRef.destroyed) {
      throw new Error('Cannot insert a destroyed View in a ViewContainer!');
    }
    const /** @type {?} */ viewRef_ = /** @type {?} */(( <ViewRef_>viewRef));
    const /** @type {?} */ viewData = viewRef_._view;
    attachEmbeddedView(this._view, this._data, index, viewData);
    viewRef_.attachToViewContainerRef(this);
    return viewRef;
  }
/**
 * @param {?} viewRef
 * @param {?} currentIndex
 * @return {?}
 */
move(viewRef: ViewRef_, currentIndex: number): ViewRef {
    if (viewRef.destroyed) {
      throw new Error('Cannot move a destroyed View in a ViewContainer!');
    }
    const /** @type {?} */ previousIndex = this._embeddedViews.indexOf(viewRef._view);
    moveEmbeddedView(this._data, previousIndex, currentIndex);
    return viewRef;
  }
/**
 * @param {?} viewRef
 * @return {?}
 */
indexOf(viewRef: ViewRef): number {
    return this._embeddedViews.indexOf(( /** @type {?} */((<ViewRef_>viewRef)))._view);
  }
/**
 * @param {?=} index
 * @return {?}
 */
remove(index?: number): void {
    const /** @type {?} */ viewData = detachEmbeddedView(this._data, index);
    if (viewData) {
      Services.destroyView(viewData);
    }
  }
/**
 * @param {?=} index
 * @return {?}
 */
detach(index?: number): ViewRef|null {
    const /** @type {?} */ view = detachEmbeddedView(this._data, index);
    return view ? new ViewRef_(view) : null;
  }
}

function ViewContainerRef__tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
ViewContainerRef_.prototype._embeddedViews;
/** @type {?} */
ViewContainerRef_.prototype._view;
/** @type {?} */
ViewContainerRef_.prototype._elDef;
/** @type {?} */
ViewContainerRef_.prototype._data;
}

/**
 * @param {?} view
 * @return {?}
 */
export function createChangeDetectorRef(view: ViewData): ChangeDetectorRef {
  return new ViewRef_(view);
}
export class ViewRef_ implements EmbeddedViewRef<any>, InternalViewRef {
/**
 * \@internal
 */
_view: ViewData;
private _viewContainerRef: ViewContainerRef|null;
private _appRef: ApplicationRef|null;
/**
 * @param {?} _view
 */
constructor(_view: ViewData) {
    this._view = _view;
    this._viewContainerRef = null;
    this._appRef = null;
  }
/**
 * @return {?}
 */
get rootNodes(): any[] { return rootRenderNodes(this._view); }
/**
 * @return {?}
 */
get context() { return this._view.context; }
/**
 * @return {?}
 */
get destroyed(): boolean { return (this._view.state & ViewState.Destroyed) !== 0; }
/**
 * @return {?}
 */
markForCheck(): void { markParentViewsForCheck(this._view); }
/**
 * @return {?}
 */
detach(): void { this._view.state &= ~ViewState.Attached; }
/**
 * @return {?}
 */
detectChanges(): void {
    const /** @type {?} */ fs = this._view.root.rendererFactory;
    if (fs.begin) {
      fs.begin();
    }
    Services.checkAndUpdateView(this._view);
    if (fs.end) {
      fs.end();
    }
  }
/**
 * @return {?}
 */
checkNoChanges(): void { Services.checkNoChangesView(this._view); }
/**
 * @return {?}
 */
reattach(): void { this._view.state |= ViewState.Attached; }
/**
 * @param {?} callback
 * @return {?}
 */
onDestroy(callback: Function) {
    if (!this._view.disposables) {
      this._view.disposables = [];
    }
    this._view.disposables.push( /** @type {?} */((<any>callback)));
  }
/**
 * @return {?}
 */
destroy() {
    if (this._appRef) {
      this._appRef.detachView(this);
    } else if (this._viewContainerRef) {
      this._viewContainerRef.detach(this._viewContainerRef.indexOf(this));
    }
    Services.destroyView(this._view);
  }
/**
 * @return {?}
 */
detachFromAppRef() {
    this._appRef = null;
    renderDetachView(this._view);
    Services.dirtyParentQueries(this._view);
  }
/**
 * @param {?} appRef
 * @return {?}
 */
attachToAppRef(appRef: ApplicationRef) {
    if (this._viewContainerRef) {
      throw new Error('This view is already attached to a ViewContainer!');
    }
    this._appRef = appRef;
  }
/**
 * @param {?} vcRef
 * @return {?}
 */
attachToViewContainerRef(vcRef: ViewContainerRef) {
    if (this._appRef) {
      throw new Error('This view is already attached directly to the ApplicationRef!');
    }
    this._viewContainerRef = vcRef;
  }
}

function ViewRef__tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
ViewRef_.prototype._view;
/** @type {?} */
ViewRef_.prototype._viewContainerRef;
/** @type {?} */
ViewRef_.prototype._appRef;
}

/**
 * @param {?} view
 * @param {?} def
 * @return {?}
 */
export function createTemplateData(view: ViewData, def: NodeDef): TemplateData {
  return new TemplateRef_(view, def);
}
class TemplateRef_ extends TemplateRef<any> implements TemplateData {
/**
 * \@internal
 */
_projectedViews: ViewData[];
/**
 * @param {?} _parentView
 * @param {?} _def
 */
constructor(private _parentView: ViewData,
private _def: NodeDef) { super(); }
/**
 * @param {?} context
 * @return {?}
 */
createEmbeddedView(context: any): EmbeddedViewRef<any> {
    return new ViewRef_(Services.createEmbeddedView(
        this._parentView, this._def, /** @type {?} */(( /** @type {?} */(( this._def.element)).template)), context));
  }
/**
 * @return {?}
 */
get elementRef(): ElementRef {
    return new ElementRef(asElementData(this._parentView, this._def.index).renderElement);
  }
}

function TemplateRef__tsickle_Closure_declarations() {
/**
 * \@internal
 * @type {?}
 */
TemplateRef_.prototype._projectedViews;
/** @type {?} */
TemplateRef_.prototype._parentView;
/** @type {?} */
TemplateRef_.prototype._def;
}

/**
 * @param {?} view
 * @param {?} elDef
 * @return {?}
 */
export function createInjector(view: ViewData, elDef: NodeDef): Injector {
  return new Injector_(view, elDef);
}
class Injector_ implements Injector {
/**
 * @param {?} view
 * @param {?} elDef
 */
constructor(private view: ViewData,
private elDef: NodeDef|null) {}
/**
 * @param {?} token
 * @param {?=} notFoundValue
 * @return {?}
 */
get(token: any, notFoundValue: any = Injector.THROW_IF_NOT_FOUND): any {
    const /** @type {?} */ allowPrivateServices =
        this.elDef ? (this.elDef.flags & NodeFlags.ComponentView) !== 0 : false;
    return Services.resolveDep(
        this.view, this.elDef, allowPrivateServices,
        {flags: DepFlags.None, token, tokenKey: tokenKey(token)}, notFoundValue);
  }
}

function Injector__tsickle_Closure_declarations() {
/** @type {?} */
Injector_.prototype.view;
/** @type {?} */
Injector_.prototype.elDef;
}

/**
 * @param {?} view
 * @param {?} index
 * @return {?}
 */
export function nodeValue(view: ViewData, index: number): any {
  const /** @type {?} */ def = view.def.nodes[index];
  if (def.flags & NodeFlags.TypeElement) {
    const /** @type {?} */ elData = asElementData(view, def.index);
    return /** @type {?} */(( def.element)).template ? elData.template : elData.renderElement;
  } else if (def.flags & NodeFlags.TypeText) {
    return asTextData(view, def.index).renderText;
  } else if (def.flags & (NodeFlags.CatProvider | NodeFlags.TypePipe)) {
    return asProviderData(view, def.index).instance;
  }
  throw new Error(`Illegal state: read nodeValue for node index ${index}`);
}
/**
 * @param {?} view
 * @return {?}
 */
export function createRendererV1(view: ViewData): RendererV1 {
  return new RendererAdapter(view.renderer);
}
class RendererAdapter implements RendererV1 {
/**
 * @param {?} delegate
 */
constructor(private delegate: Renderer2) {}
/**
 * @param {?} selectorOrNode
 * @return {?}
 */
selectRootElement(selectorOrNode: string|Element): Element {
    return this.delegate.selectRootElement(selectorOrNode);
  }
/**
 * @param {?} parent
 * @param {?} namespaceAndName
 * @return {?}
 */
createElement(parent: Element|DocumentFragment, namespaceAndName: string): Element {
    const [ns, name] = splitNamespace(namespaceAndName);
    const /** @type {?} */ el = this.delegate.createElement(name, ns);
    if (parent) {
      this.delegate.appendChild(parent, el);
    }
    return el;
  }
/**
 * @param {?} hostElement
 * @return {?}
 */
createViewRoot(hostElement: Element): Element|DocumentFragment { return hostElement; }
/**
 * @param {?} parentElement
 * @return {?}
 */
createTemplateAnchor(parentElement: Element|DocumentFragment): Comment {
    const /** @type {?} */ comment = this.delegate.createComment('');
    if (parentElement) {
      this.delegate.appendChild(parentElement, comment);
    }
    return comment;
  }
/**
 * @param {?} parentElement
 * @param {?} value
 * @return {?}
 */
createText(parentElement: Element|DocumentFragment, value: string): any {
    const /** @type {?} */ node = this.delegate.createText(value);
    if (parentElement) {
      this.delegate.appendChild(parentElement, node);
    }
    return node;
  }
/**
 * @param {?} parentElement
 * @param {?} nodes
 * @return {?}
 */
projectNodes(parentElement: Element|DocumentFragment, nodes: Node[]) {
    for (let /** @type {?} */ i = 0; i < nodes.length; i++) {
      this.delegate.appendChild(parentElement, nodes[i]);
    }
  }
/**
 * @param {?} node
 * @param {?} viewRootNodes
 * @return {?}
 */
attachViewAfter(node: Node, viewRootNodes: Node[]) {
    const /** @type {?} */ parentElement = this.delegate.parentNode(node);
    const /** @type {?} */ nextSibling = this.delegate.nextSibling(node);
    for (let /** @type {?} */ i = 0; i < viewRootNodes.length; i++) {
      this.delegate.insertBefore(parentElement, viewRootNodes[i], nextSibling);
    }
  }
/**
 * @param {?} viewRootNodes
 * @return {?}
 */
detachView(viewRootNodes: (Element|Text|Comment)[]) {
    for (let /** @type {?} */ i = 0; i < viewRootNodes.length; i++) {
      const /** @type {?} */ node = viewRootNodes[i];
      const /** @type {?} */ parentElement = this.delegate.parentNode(node);
      this.delegate.removeChild(parentElement, node);
    }
  }
/**
 * @param {?} hostElement
 * @param {?} viewAllNodes
 * @return {?}
 */
destroyView(hostElement: Element|DocumentFragment, viewAllNodes: Node[]) {
    for (let /** @type {?} */ i = 0; i < viewAllNodes.length; i++) { /** @type {?} */((
      this.delegate.destroyNode))(viewAllNodes[i]);
    }
  }
/**
 * @param {?} renderElement
 * @param {?} name
 * @param {?} callback
 * @return {?}
 */
listen(renderElement: any, name: string, callback: Function): Function {
    return this.delegate.listen(renderElement, name, /** @type {?} */(( <any>callback)));
  }
/**
 * @param {?} target
 * @param {?} name
 * @param {?} callback
 * @return {?}
 */
listenGlobal(target: string, name: string, callback: Function): Function {
    return this.delegate.listen(target, name, /** @type {?} */(( <any>callback)));
  }
/**
 * @param {?} renderElement
 * @param {?} propertyName
 * @param {?} propertyValue
 * @return {?}
 */
setElementProperty(
      renderElement: Element|DocumentFragment, propertyName: string, propertyValue: any): void {
    this.delegate.setProperty(renderElement, propertyName, propertyValue);
  }
/**
 * @param {?} renderElement
 * @param {?} namespaceAndName
 * @param {?} attributeValue
 * @return {?}
 */
setElementAttribute(renderElement: Element, namespaceAndName: string, attributeValue: string):
      void {
    const [ns, name] = splitNamespace(namespaceAndName);
    if (attributeValue != null) {
      this.delegate.setAttribute(renderElement, name, attributeValue, ns);
    } else {
      this.delegate.removeAttribute(renderElement, name, ns);
    }
  }
/**
 * @param {?} renderElement
 * @param {?} propertyName
 * @param {?} propertyValue
 * @return {?}
 */
setBindingDebugInfo(renderElement: Element, propertyName: string, propertyValue: string): void {}
/**
 * @param {?} renderElement
 * @param {?} className
 * @param {?} isAdd
 * @return {?}
 */
setElementClass(renderElement: Element, className: string, isAdd: boolean): void {
    if (isAdd) {
      this.delegate.addClass(renderElement, className);
    } else {
      this.delegate.removeClass(renderElement, className);
    }
  }
/**
 * @param {?} renderElement
 * @param {?} styleName
 * @param {?} styleValue
 * @return {?}
 */
setElementStyle(renderElement: HTMLElement, styleName: string, styleValue: string): void {
    if (styleValue != null) {
      this.delegate.setStyle(renderElement, styleName, styleValue);
    } else {
      this.delegate.removeStyle(renderElement, styleName);
    }
  }
/**
 * @param {?} renderElement
 * @param {?} methodName
 * @param {?} args
 * @return {?}
 */
invokeElementMethod(renderElement: Element, methodName: string, args: any[]): void {
    ( /** @type {?} */((renderElement as any)))[methodName].apply(renderElement, args);
  }
/**
 * @param {?} renderNode
 * @param {?} text
 * @return {?}
 */
setText(renderNode: Text, text: string): void { this.delegate.setValue(renderNode, text); }
/**
 * @return {?}
 */
animate(): any { throw new Error('Renderer.animate is no longer supported!'); }
}

function RendererAdapter_tsickle_Closure_declarations() {
/** @type {?} */
RendererAdapter.prototype.delegate;
}

/**
 * @param {?} moduleType
 * @param {?} parent
 * @param {?} bootstrapComponents
 * @param {?} def
 * @return {?}
 */
export function createNgModuleRef(
    moduleType: Type<any>, parent: Injector, bootstrapComponents: Type<any>[],
    def: NgModuleDefinition): NgModuleRef<any> {
  return new NgModuleRef_(moduleType, parent, bootstrapComponents, def);
}
class NgModuleRef_ implements NgModuleData, InternalNgModuleRef<any> {
private _destroyListeners: (() => void)[] = [];
private _destroyed: boolean = false;
public _providers: any[];
/**
 * @param {?} _moduleType
 * @param {?} _parent
 * @param {?} _bootstrapComponents
 * @param {?} _def
 */
constructor(
private _moduleType: Type<any>,
public _parent: Injector,
public _bootstrapComponents: Type<any>[],
public _def: NgModuleDefinition) {
    initNgModule(this);
  }
/**
 * @param {?} token
 * @param {?=} notFoundValue
 * @return {?}
 */
get(token: any, notFoundValue: any = Injector.THROW_IF_NOT_FOUND): any {
    return resolveNgModuleDep(
        this, {token: token, tokenKey: tokenKey(token), flags: DepFlags.None}, notFoundValue);
  }
/**
 * @return {?}
 */
get instance() { return this.get(this._moduleType); }
/**
 * @return {?}
 */
get componentFactoryResolver() { return this.get(ComponentFactoryResolver); }
/**
 * @return {?}
 */
get injector(): Injector { return this; }
/**
 * @return {?}
 */
destroy(): void {
    if (this._destroyed) {
      throw new Error(
          `The ng module ${stringify(this.instance.constructor)} has already been destroyed.`);
    }
    this._destroyed = true;
    callNgModuleLifecycle(this, NodeFlags.OnDestroy);
    this._destroyListeners.forEach((listener) => listener());
  }
/**
 * @param {?} callback
 * @return {?}
 */
onDestroy(callback: () => void): void { this._destroyListeners.push(callback); }
}

function NgModuleRef__tsickle_Closure_declarations() {
/** @type {?} */
NgModuleRef_.prototype._destroyListeners;
/** @type {?} */
NgModuleRef_.prototype._destroyed;
/** @type {?} */
NgModuleRef_.prototype._providers;
/** @type {?} */
NgModuleRef_.prototype._moduleType;
/** @type {?} */
NgModuleRef_.prototype._parent;
/** @type {?} */
NgModuleRef_.prototype._bootstrapComponents;
/** @type {?} */
NgModuleRef_.prototype._def;
}

